import React, { useCallback, useEffect, useState } from 'react';
import FormContainer from 'components/LoanForm/FormContainer';
import { Divider } from '@material-ui/core';
import { ReactComponent as TransferIcon } from 'images/transfer-rounded.svg';
import Expander from 'components/Expander';
import Button from 'components/Button';
import { formatMonetaryAmount } from 'utils/formatMonetaryAmount';

import { useDispatch, useSelector } from 'react-redux';
import { getApplicationData } from 'selectors/getApplicationData';
import { getAmountPerPaycheck } from 'selectors/getAmountPerPaycheck';
import { clearAdditionalFundsData, HardOfferData, Tradeline } from 'handlers/applicationData';
import { SelectedTradeline } from 'api/ApplicationDataApi';
import useDebtConsolidationState, { SelectedTradelineState } from 'hooks/useDebtConsolidationState';
import useDispatchWithUnwrap from 'hooks/useDispatchWithUnwrap';
import { getApplicationApr, getAmountPerPaycheck as getPartialPaymentThunk, updateSelectedTradelines } from 'thunks';
import { FlowComponentType } from 'routes/types';
import { SelectDebtsResult } from 'enums/FlowNextResults';
import Loader from 'components/Loader';
import { meetsAdditionalFundsThreshold } from 'components/AdditionalFunds/additionalFundsUtils';
import { getPaymentMethodLabel } from 'components/PaymentMethodSwitch';

import TradeLineItem from './components/TradeLineItem';

import styles from './SelectDebts.module.scss';

const getPayoffTotal = (tradelines: Tradeline[]) =>
  tradelines.reduce((acc, item) => acc + (item.selectedForConsolidation ? item.balanceToConsolidate : 0), 0);

const SelectDebts = ({ handleNext }: FlowComponentType) => {
  const { application, additionalFundsData } = useSelector(getApplicationData);
  const isEligibleForAdditionalFunds = application?.additionalFundsEligibility;
  const { amountPerPaycheck, isLoading: loadingAmountPerPaycheck, isError: isErrorAmountPerPaycheck } = useSelector(
    getAmountPerPaycheck,
  );
  const [submitting, setSubmitting] = useState(false);

  const dispatch = useDispatch();
  const dispatchWithUnwrap = useDispatchWithUnwrap();

  const data = application?.hardOffer as HardOfferData;

  const {
    tradelinesModifications,
    payOff,
    keepIt,
    tradelines,
    cantConsolidate,
    toggleTradelineConsolidation,
    updateTradelinePayoffAmount,
    resetModifications,
  } = useDebtConsolidationState({
    data,
    maxLoanAmount: application?.loanConsolidationAmount ?? application?.maxLoanAmount ?? 0,
  });

  const handleSelectTradeLine = (selectedTradeline: SelectedTradeline) => {
    toggleTradelineConsolidation(selectedTradeline);
  };

  const updateTradelines = async (newState: SelectedTradelineState) => {
    setSubmitting(true);
    try {
      await dispatchWithUnwrap(
        updateSelectedTradelines({
          applicationId: application!.id,
          tradelinesToUpdate: Object.values(newState),
        }),
      );
      await dispatchWithUnwrap(getApplicationApr(application!.id));
      resetModifications();
    } catch (e) {
      // Error is already handled by dispatchWithUnwrap
    } finally {
      setSubmitting(false);
    }
  };

  const handleTradelinePayOffAmountUpdate = async (id: string, amount: number) => {
    if (tradelines.find((tl) => tl.userOverriddenBalanceToConsolidate === amount)) return;
    const newSelectedTradelines = updateTradelinePayoffAmount(id, amount);
    if (!newSelectedTradelines) return;
    updateTradelines(newSelectedTradelines);
  };

  const handleSelectedTradelinesOnClick = useCallback(async () => {
    await updateTradelines(tradelinesModifications);
  }, [tradelinesModifications, application]);

  const payoffTotal = getPayoffTotal(tradelines);

  const { planneryLoan } = data;

  const lines = [
    { label: 'Months', value: <p className={styles.summaryItemValue}>{planneryLoan.term}</p> },
    {
      label: 'Debts paid',
      value: (
        <p className={styles.summaryItemValue}>
          {tradelines.filter((item) => item.selectedForConsolidation && item.balanceToConsolidate > 0).length}
        </p>
      ),
    },
  ];

  if (!isErrorAmountPerPaycheck) {
    lines.unshift({
      label: `Payment per ${getPaymentMethodLabel(application?.paymentMethod)}`,
      value:
        loadingAmountPerPaycheck || !amountPerPaycheck || submitting ? (
          <Loader color="#9d86f9" size={27.5} />
        ) : (
          <p className={styles.summaryItemValue}>
            {formatMonetaryAmount(amountPerPaycheck.amountPerPaycheck.paymentAmount, true)}
          </p>
        ),
    });
  }

  const maximumPayoffAmountSelected = payoffTotal >= application?.loanConsolidationAmount!;

  const maxLoanAmount = application?.loanConsolidationAmount ?? application?.maxLoanAmount ?? 0;

  // Calculate current total consolidated for a specific tradeline (excluding the tradeline itself)
  const calculateCurrentTotalForTradeline = (tradelineId: string) =>
    tradelines
      .filter((tl) => tl.id !== tradelineId && tl.selectedForConsolidation)
      .reduce((acc, tl) => acc + tl.balanceToConsolidate, 0);

  const submit = async () => {
    const hasModifications = Object.keys(tradelinesModifications).length > 0;
    if (hasModifications) await handleSelectedTradelinesOnClick();

    if (
      !isEligibleForAdditionalFunds ||
      (!hasModifications && additionalFundsData && !meetsAdditionalFundsThreshold(additionalFundsData))
    ) {
      return handleNext(SelectDebtsResult.FinalizeReviewOffer);
    }

    if (hasModifications && additionalFundsData) dispatch(clearAdditionalFundsData());
    handleNext(SelectDebtsResult.FundsRemaining);
  };

  useEffect(() => {
    if (!payoffTotal || !application?.id) return;
    dispatchWithUnwrap(
      getPartialPaymentThunk({
        applicationId: application.id,
        amount: payoffTotal,
      }),
    );
  }, [payoffTotal, application?.id]);

  const summaryTitle = `${maximumPayoffAmountSelected ? 'Max payoff' : 'Payoff'} selected`;
  const showDirectPayBadge = payOff?.some((tl) => tl.weWillPay);

  const extractTradelineItempProps = (tl: Tradeline) => ({
    tradeline: tl,
    disabled: submitting,
    maxLoanAmount,
    currentTotalConsolidated: calculateCurrentTotalForTradeline(tl.id),
    onSelect: handleSelectTradeLine,
    updateTradelinePayoffAmount: handleTradelinePayOffAmountUpdate,
  });

  return (
    <FormContainer title="Select the debts you want to pay off:">
      <div className={styles.summaryContainer}>
        <div className={styles.summaryTitleContainer}>
          <h4>{summaryTitle} </h4>
          <p className={styles.summaryTitleValue}>{formatMonetaryAmount(payoffTotal)}</p>
        </div>
        <Divider />
        <div>
          {lines.map((item, index) => (
            <div className={styles.summaryItem} key={index}>
              <p className={styles.summaryItemLabel}>{item.label}</p>
              {item.value}
            </div>
          ))}
        </div>
      </div>
      <div className={styles.card}>
        <div>
          <h4>Top picks</h4>
          <p className={styles.topPicksDescription}>Likely the most savings.</p>
        </div>
        {payOff?.map((tl) => (
          <div className={styles.tradeLine} key={tl.id}>
            <TradeLineItem {...extractTradelineItempProps(tl)} />
          </div>
        ))}

        {showDirectPayBadge && (
          <div className={styles.directPayTextContainer}>
            <TransferIcon className={styles.transferIcon} />

            <p className={styles.directPayText}>
              <span className={styles.directPayTextTitle}>Direct Pay. </span>
              Plannery will pay off these debts directly for you.
            </p>
          </div>
        )}
      </div>
      <div className={styles.card}>
        <Expander
          label={<h4 className={styles.label}>Other accounts</h4>}
          content={
            <div className={styles.tradeLineListContainer}>
              <p>Accounts you can include based on your own priorities and preferences.</p>
              {keepIt?.map((tl) => (
                <div className={styles.tradeLine} key={tl.id}>
                  <TradeLineItem {...extractTradelineItempProps(tl)} />
                </div>
              ))}
            </div>
          }
          labelClassname={styles.labelContainer}
          openClassname={styles.tradelinesContainer}
          translateArrow={false}
          isOpenByDefault={keepIt.some((t) => t.selectedForConsolidation)}
        />
      </div>
      <div className={styles.card}>
        <Expander
          label={<h4 className={styles.label}>Not eligible</h4>}
          content={
            <div className={styles.tradeLineListContainer}>
              <p>
                Plannery is not able to consolidate car, home, student debt, joint or authorized accounts, or delinquent
                accounts with this loan.
              </p>
              {cantConsolidate?.map((tl) => (
                <div className={styles.tradeLine} key={tl.id}>
                  <TradeLineItem {...extractTradelineItempProps(tl)} onSelect={undefined} />
                </div>
              ))}
            </div>
          }
          labelClassname={styles.labelContainer}
          translateArrow={false}
          openClassname={styles.tradelinesContainer}
        />
      </div>
      <Button onClick={submit} isLoading={submitting}>
        Next
      </Button>
      <p className={styles.contactUsText}>Contact us if you need help or something doesn't look right.</p>
    </FormContainer>
  );
};

export default SelectDebts;
